import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput, {
  formatLatexPreview,
} from "../../../../components/inputs/FractionInput";

function randInt(min, max) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

function pickOne(arr) {
  return arr[randInt(0, arr.length - 1)];
}

function parseVal(v) {
  if (v && typeof v === "object" && Number.isFinite(v.parsed)) return v.parsed;
  return NaN;
}

const CONTEXTS = [
  { place: "do kina" },
  { place: "na koncert" },
  { place: "na przedstawienie" },
  { place: "na spektakl" },
];

export function generateTask() {
  const ctx = pickOne(CONTEXTS);

  const normalCount = randInt(8, 30);
  const ulgCount = randInt(6, 24);
  const totalCount = normalCount + ulgCount;

  const normalPrice = randInt(18, 42);
  const ulgPrice = randInt(8, normalPrice - 4);
  const totalAmount = normalCount * normalPrice + ulgCount * ulgPrice;

  return {
    type: "TicketsSalesTask",
    data: {
      place: ctx.place,
      normalCount,
      ulgCount,
      totalCount,
      normalPrice,
      ulgPrice,
      totalAmount,
    },
  };
}

export function renderTask({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.6 }}>
      <p>
        Sprzedano łącznie <InlineMath math={`${data.totalCount}`} /> biletów{" "}
        {data.place}.
      </p>
      <p>
        Bilet normalny kosztuje <InlineMath math={`${data.normalPrice}`} /> zł,
        a bilet ulgowy kosztuje <InlineMath math={`${data.ulgPrice}`} /> zł.
      </p>
      <p>
        Wpływy wyniosły <InlineMath math={`${data.totalAmount}`} /> zł. Znajdź,
        ile sprzedano biletów normalnych i ile biletów ulgowych.
      </p>
      <p>
        Przyjmij: <InlineMath math="n" /> - liczba biletów normalnych,{" "}
        <InlineMath math="u" /> - liczba biletów ulgowych.
      </p>
    </div>
  );
}

export function renderInput(_task, userAnswer = {}, onChange, disabled) {
  const nVal = userAnswer?.n || "";
  const uVal = userAnswer?.u || "";

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center gap-2">
        <InlineMath math={"n ="} />
        <FractionInput
          value={nVal}
          onChange={(v) => onChange({ ...userAnswer, n: v })}
          disabled={disabled}
          label="n"
          placeholder="np. 12"
        />
      </div>
      <div className="flex items-center gap-2">
        <InlineMath math={"u ="} />
        <FractionInput
          value={uVal}
          onChange={(v) => onChange({ ...userAnswer, u: v })}
          disabled={disabled}
          label="u"
          placeholder="np. 10"
        />
      </div>
    </div>
  );
}

export function checkAnswer({ data }, userAnswer) {
  const un = parseVal(userAnswer?.n);
  const uu = parseVal(userAnswer?.u);
  if (!Number.isFinite(un) || !Number.isFinite(uu)) return { correct: false };
  const okN = Math.abs(un - data.normalCount) < 0.01;
  const okU = Math.abs(uu - data.ulgCount) < 0.01;
  return { correct: okN && okU };
}

export function renderAnswer({ data }) {
  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      <div>
        <InlineMath math={`n = ${data.normalCount}`} /> (bilety normalne)
      </div>
      <div>
        <InlineMath math={`u = ${data.ulgCount}`} /> (bilety ulgowe)
      </div>
      <div style={{ marginTop: "0.2rem" }}>
        <strong>Odpowiedź:</strong> sprzedano {data.normalCount} biletów
        normalnych i {data.ulgCount} biletów ulgowych.
      </div>
    </div>
  );
}

export function renderUserAnswer(taskOrUserAnswer, maybeUserAnswer) {
  const userAnswer =
    maybeUserAnswer === undefined ? taskOrUserAnswer : maybeUserAnswer;
  const nRaw = userAnswer?.n?.raw || "";
  const uRaw = userAnswer?.u?.raw || "";
  if (!nRaw && !uRaw) return <i>(brak odpowiedzi)</i>;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.35 }}>
      {nRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(nRaw, "n")} />
        </div>
      ) : (
        <i>(brak n)</i>
      )}
      {uRaw ? (
        <div>
          <InlineMath math={formatLatexPreview(uRaw, "u")} />
        </div>
      ) : (
        <i>(brak u)</i>
      )}
    </div>
  );
}

export function renderExplanation({ data }) {
  const {
    totalCount,
    normalPrice,
    ulgPrice,
    totalAmount,
    normalCount,
    ulgCount,
  } = data;

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1:</strong> Układamy układ równań:
      </p>
      <BlockMath
        math={String.raw`\begin{cases}n+u=${totalCount}\\${normalPrice}n+${ulgPrice}u=${totalAmount}\end{cases}`}
      />

      <p>
        <strong>Krok 2:</strong> Z pierwszego równania:{" "}
        <InlineMath math={`n = ${totalCount} - u`} />.
      </p>

      <p>
        <strong>Krok 3:</strong> Podstawiamy do drugiego:
      </p>
      <BlockMath
        math={`${normalPrice}(${totalCount}-u)+${ulgPrice}u=${totalAmount}`}
      />
      <BlockMath
        math={`${normalPrice * totalCount}-${normalPrice}u+${ulgPrice}u=${totalAmount}`}
      />
      <BlockMath
        math={`${ulgPrice - normalPrice}u=${totalAmount - normalPrice * totalCount}`}
      />
      <BlockMath math={`u=${ulgCount}`} />

      <p>
        <strong>Krok 4:</strong> Liczymy <InlineMath math="n" />:
      </p>
      <BlockMath math={`n=${totalCount}-${ulgCount}=${normalCount}`} />

      <p>
        <strong>Odpowiedź:</strong> sprzedano {normalCount} biletów
        normalnych i {ulgCount} biletów ulgowych.
      </p>
    </div>
  );
}
